import React, { useState, useEffect, useRef, useCallback } from 'react';
import ServiceNavigation from '../ServiceNavigation.jsx';
import BrowserCheck from '../BrowserCheck';
import TopPanel from '../TopPanel';
import SynopsisPanelV3 from '../SynopsisPanelV3';
import BlogReportViewer from './BlogReportViewer';
import UrlInputPanel from './UrlInputPanel';
import PipelineStatus from './PipelineStatus';
import { sendAnalytics } from '../../resources/rum-provider';
import { BLOG_CHECKER_ENDPOINT } from '../../resources/prod-env.jsx';
import { io } from 'socket.io-client';

import {
    AppLayout,
    SpaceBetween,
    Alert,
} from '@cloudscape-design/components';

import '../../styles/form.scss';
import './blog_checker.scss';

const demoModule = "BlogChecker";
const demoType   = "tool";

// Default — the backend's change_threshold_percent. Only used by the UI for
// rendering the threshold tick on the change-% bar.
const DEFAULT_CHANGE_THRESHOLD = 30;

// localStorage key for the most recently submitted URL list. The pipeline
// runs to completion server-side even after the browser tab closes; the
// result lands in the cache. By restoring the URLs the user submitted
// earlier we let them come back, hit "Start Review", and see the cached
// result instantly (no need to re-type anything).
const RECENT_URLS_KEY = 'blog-checker:recent-urls';

function loadRecentUrlEntries() {
    try {
        const raw = window.localStorage.getItem(RECENT_URLS_KEY);
        if (!raw) return null;
        const urls = JSON.parse(raw);
        if (!Array.isArray(urls) || !urls.length) return null;
        return urls.slice(0, 50).map((url, i) => ({ id: i + 1, url: String(url) }));
    } catch {
        return null;
    }
}

function saveRecentUrlEntries(entries) {
    try {
        const urls = entries.map(e => e.url.trim()).filter(Boolean);
        if (urls.length) {
            window.localStorage.setItem(RECENT_URLS_KEY, JSON.stringify(urls));
        } else {
            window.localStorage.removeItem(RECENT_URLS_KEY);
        }
    } catch {
        /* localStorage unavailable / quota — ignore */
    }
}

export default function BlogChecker(props) {
    const [urlEntries, setUrlEntries] = useState(
        () => loadRecentUrlEntries() || [{ id: 1, url: '' }]
    );
    const [isProcessing, setIsProcessing]       = useState(false);
    const [results, setResults]                 = useState([]);
    const [pipelineStages, setPipelineStages]   = useState({});
    const [streamingDocs, setStreamingDocs]     = useState({});
    const [error, setError]                     = useState(null);
    const [selectedResultIdx, setSelectedResultIdx] = useState(null);
    const [connected, setConnected]             = useState(false);

    const pendingJobsRef = useRef(new Set());
    const socketRef      = useRef(null);

    // ── Socket.io connection ─────────────────────────────────────────────────
    useEffect(() => {
        const socket = io(BLOG_CHECKER_ENDPOINT, {
            transports:   ['websocket'],
            autoConnect:  true,
            reconnection: true,
        });

        socket.on('connect',       () => { setConnected(true);  setError(null); });
        socket.on('disconnect',    () => setConnected(false));
        socket.on('connect_error', (err) => {
            setError(`Connection error: ${err.message}`);
            setConnected(false);
        });

        socket.on('batch_started', ({ jobs }) => {
            jobs.forEach(({ jobId, url }) => {
                pendingJobsRef.current.add(jobId);
                setPipelineStages(prev => ({
                    ...prev,
                    [jobId]: { stage: 'fetch', iterations: 0, arc_count: 0, title: url },
                }));
                setResults(prev => {
                    if (prev.find(r => r.jobId === jobId)) return prev;
                    const next = [...prev, {
                        jobId, title: url, url,
                        authors: '', date: '', services: '',
                        report_md: null, score: null,
                        change_percent: null, recommendation: null,
                        update_blurb: '', suggestion_count: null,
                        iterations: null, arc_count: 0,
                        status: 'pending', error: null,
                    }];
                    if (next.length === 1) setSelectedResultIdx(0);
                    return next;
                });
            });
        });

        socket.on('stage_update', (data) => {
            const { jobId } = data;
            if (!jobId) return;
            setPipelineStages(prev => ({
                ...prev,
                [jobId]: {
                    ...prev[jobId],
                    stage:      data.stage,
                    iterations: data.iterations ?? prev[jobId]?.iterations ?? 0,
                    arc_count:  data.arc_count  ?? prev[jobId]?.arc_count  ?? 0,
                    title:      data.title      || prev[jobId]?.title || '',
                },
            }));
            if ('title' in data || 'authors' in data || 'date' in data || 'services' in data) {
                setResults(prev => prev.map(r => {
                    if (r.jobId !== jobId) return r;
                    return {
                        ...r,
                        title:    data.title    !== undefined ? data.title    : r.title,
                        authors:  data.authors  !== undefined ? data.authors  : r.authors,
                        date:     data.date     !== undefined ? data.date     : r.date,
                        services: data.services !== undefined ? data.services : r.services,
                    };
                }));
            }
        });

        socket.on('doc_chunk', (data) => {
            const { jobId, content } = data;
            if (!jobId) return;
            setStreamingDocs(prev => ({ ...prev, [jobId]: (prev[jobId] || '') + (content || '') }));
        });

        socket.on('result', (data) => {
            const { jobId } = data;
            if (!jobId) return;

            setPipelineStages(prev => ({
                ...prev,
                [jobId]: { ...prev[jobId], stage: data.status === 'success' ? 'done' : 'failed' },
            }));

            setResults(prev => {
                const idx = prev.findIndex(r => r.jobId === jobId);
                const existing = prev[idx] || {};
                const updated = {
                    jobId,
                    title:            data.title          || existing.url || jobId,
                    url:              existing.url        || '',
                    authors:          data.authors        || existing.authors || '',
                    date:             data.date           || existing.date    || '',
                    services:         data.services       || existing.services || '',
                    report_md:        data.report_md      || '',
                    change_percent:   data.change_percent ?? null,
                    recommendation:   data.recommendation || null,
                    update_blurb:     data.update_blurb   || '',
                    suggestion_count: data.suggestion_count ?? null,
                    cached:           data.cached === true,
                    cached_stored_at: data.cached_stored_at || null,
                    score:            data.final_score    ?? null,
                    iterations:       data.iterations     ?? null,
                    arc_count:        data.arc_count      ?? 0,
                    status:           data.status,
                    error:            data.error          || null,
                };
                const next = idx >= 0
                    ? prev.map((r, i) => i === idx ? updated : r)
                    : [...prev, updated];
                if (next.filter(r => r.status !== 'pending').length === 1) {
                    setSelectedResultIdx(next.findIndex(r => r.status !== 'pending'));
                }
                return next;
            });

            setStreamingDocs(prev => { const n = { ...prev }; delete n[jobId]; return n; });

            pendingJobsRef.current.delete(jobId);
            if (pendingJobsRef.current.size === 0) {
                setIsProcessing(false);
            }
        });

        socket.on('error', (data) => {
            const { jobId, message } = data;
            if (jobId) {
                setResults(prev => prev.map(r =>
                    r.jobId === jobId ? { ...r, status: 'failed', error: message } : r
                ));
                setPipelineStages(prev => ({
                    ...prev,
                    [jobId]: { ...prev[jobId], stage: 'failed' },
                }));
                setStreamingDocs(prev => { const n = { ...prev }; delete n[jobId]; return n; });
                pendingJobsRef.current.delete(jobId);
                if (pendingJobsRef.current.size === 0) setIsProcessing(false);
            } else {
                setError(message || 'An error occurred');
                setIsProcessing(false);
                pendingJobsRef.current.clear();
            }
        });

        socket.on('stopped', () => {
            setIsProcessing(false);
            setStreamingDocs({});
            pendingJobsRef.current.clear();
        });

        socketRef.current = socket;
        sendAnalytics({ demo: demoModule });

        return () => {
            socket.off('connect');
            socket.off('disconnect');
            socket.off('connect_error');
            socket.off('batch_started');
            socket.off('stage_update');
            socket.off('doc_chunk');
            socket.off('result');
            socket.off('error');
            socket.off('stopped');
            socket.disconnect();
        };
    }, []);

    const handleSubmit = useCallback(() => {
        const validEntries = urlEntries.filter(e => e.url.trim());
        if (!validEntries.length) {
            setError('Add at least one URL before starting a review.');
            return;
        }
        if (!connected) {
            setError('Not connected to server. Please wait and try again.');
            return;
        }

        // Persist the submitted URLs so the user can come back later (after
        // closing the tab) and get the cached result instantly by hitting
        // "Start Review" again. The backend pipeline runs to completion
        // even after the websocket disconnects, so the result lands in the
        // server-side DDB cache and re-submission becomes a cache hit.
        saveRecentUrlEntries(validEntries);

        setIsProcessing(true);
        setError(null);
        setResults([]);
        setStreamingDocs({});
        setSelectedResultIdx(null);
        setPipelineStages({});
        pendingJobsRef.current.clear();

        sendAnalytics({ demo: demoModule, action: 'generate', urlCount: validEntries.length });

        socketRef.current.emit('generate_batch', {
            urls: validEntries.map(e => e.url.trim()),
        });
    }, [urlEntries, connected]);

    const handleStop = useCallback(() => {
        socketRef.current?.emit('stop');
        pendingJobsRef.current.clear();
    }, []);

    const handleClear = useCallback(() => {
        handleStop();
        setResults([]);
        setStreamingDocs({});
        setError(null);
        setSelectedResultIdx(null);
        setPipelineStages({});
        setUrlEntries([{ id: 1, url: '' }]);
        // Also clear the persisted URL list - "Clear" is the user explicitly
        // resetting state.
        saveRecentUrlEntries([]);
    }, [handleStop]);

    const hasResults = results.length > 0;

    return (
        <React.Fragment>
            <TopPanel label={demoModule} />
            <AppLayout
                navigation={<ServiceNavigation />}
                content={
                    <SpaceBetween direction="vertical" size="m">
                        <BrowserCheck />

                        <SynopsisPanelV3 label="Synopsis" demotype={demoType} architectureImgs={[]}>
                            Review already-published AWS blog posts for accuracy against the
                            current state of the referenced services. For each blog, the multi-agent
                            pipeline produces:
                            <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px' }}>
                                <li>A <strong>change percentage</strong> reflecting how much of the
                                blog needs rewriting</li>
                                <li>An <strong>update blurb</strong> (2–4 sentences) to prepend to
                                the live post</li>
                                <li><strong>Inline suggested changes</strong> — each with the
                                original quote, suggested replacement, and verified doc link</li>
                                <li>A recommendation to <strong>update in place</strong> or
                                <strong> create a new blog</strong> when changes exceed {DEFAULT_CHANGE_THRESHOLD}%</li>
                            </ul>
                            <br />
                            <strong>How it works:</strong> Enter one or more AWS blog URLs. The
                            pipeline (Fetch → Analyze → Critic → Revise → Judge) processes all
                            URLs <strong>in parallel</strong> and streams each review as it is
                            generated.
                            <br /><br />
                            <strong>MCP servers used:</strong>
                            <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px' }}>
                                <li><strong>AWS Knowledge MCP</strong> (<code>knowledge-mcp.global.api.aws</code>)
                                — discovers feature launches, What's New posts, and current best
                                practices that post-date the blog</li>
                                <li><strong>AWS Documentation MCP</strong>
                                (<code>awslabs.aws-documentation-mcp-server</code>) — validates
                                every replacement URL by fetching the live documentation page</li>
                            </ul>
                            <br />
                            💡 Powered by Amazon Bedrock with Claude Sonnet 4.6. Knowledge MCP
                            runs via AgentCore Gateway with Cognito JWT auth; the Documentation
                            MCP runs as a local stdio subprocess in the same container.
                        </SynopsisPanelV3>

                        {!connected && (
                            <Alert type="warning">Connecting to blog-checker service...</Alert>
                        )}

                        {error && (
                            <Alert type="error" dismissible onDismiss={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        <UrlInputPanel
                            urlEntries={urlEntries}
                            setUrlEntries={setUrlEntries}
                            isProcessing={isProcessing}
                            connected={connected}
                            onSubmit={handleSubmit}
                            onStop={handleStop}
                            onClear={handleClear}
                        />

                        {(isProcessing || Object.keys(pipelineStages).length > 0) && (
                            <PipelineStatus
                                pipelineStages={pipelineStages}
                                streamingDocs={streamingDocs}
                                isProcessing={isProcessing}
                            />
                        )}

                        {hasResults && (
                            <BlogReportViewer
                                results={results}
                                streamingDocs={streamingDocs}
                                selectedResultIdx={selectedResultIdx}
                                setSelectedResultIdx={setSelectedResultIdx}
                                changeThreshold={DEFAULT_CHANGE_THRESHOLD}
                            />
                        )}
                    </SpaceBetween>
                }
                contentType="default"
                navigationOpen={props?.navigationState}
                onNavigationChange={e => props?.setNavigationState?.(e.detail.open)}
            />
        </React.Fragment>
    );
}
